/*
 * Swym wishlist page renderer.
 *
 * Swym is used only as the data source: its SDK (window._swat) returns the list
 * of wishlisted products. We render each one with the theme's own card markup by
 * fetching {product.url}?view=wishlist-card, so the wishlist grid is visually
 * identical to a collection page (portrait image, swatches, price, quick-buy).
 */
(function () {
  // The wishlist grid markup can be present at load (the /pages/wishlist
  // template) OR injected later by the customer-dashboard app's SPA when its
  // Wishlist tab is opened. The app TEARS DOWN and RE-INJECTS a fresh grid each
  // time you leave and return to the Wishlist tab, so we must re-init for every
  // new grid element — not just the first. Track the grid by identity and run
  // init() whenever a grid we haven't booted yet appears.
  var bootedGrid = null;
  var teardown = null; // cleanup for the previous init (listeners + poll)

  function maybeBoot() {
    var grid = document.querySelector('[data-wishlist-grid]');
    if (!grid || grid === bootedGrid) return;
    // A fresh grid was injected (or first load). Tear down the previous init's
    // document-level listeners / poll so they don't stack and target stale DOM.
    if (teardown) { teardown(); teardown = null; }
    bootedGrid = grid;
    teardown = init();
  }

  maybeBoot();
  new MutationObserver(maybeBoot).observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  // init() returns a teardown function that detaches everything it attached to
  // `document` and clears its poll, so a re-init for a new grid starts clean.
  function init() {
  var grid = document.querySelector('[data-wishlist-grid]');
  if (!grid) return;

  var loadingEl = document.querySelector('[data-wishlist-loading]');
  var emptyEl = document.querySelector('[data-wishlist-empty]');

  function show(el, visible) {
    if (el) el.hidden = !visible;
  }

  function setStateLoading() {
    show(loadingEl, true);
    show(emptyEl, false);
    show(grid, false);
  }

  function setStateEmpty() {
    show(loadingEl, false);
    show(emptyEl, true);
    show(grid, false);
    // Drop any stale cards: a leftover card whose item was already removed
    // from Swym would re-ADD on heart click (the toggle sees "not wishlisted").
    grid.innerHTML = '';
  }

  function setStateGrid() {
    show(loadingEl, false);
    show(emptyEl, false);
    show(grid, true);
  }

  // Build the per-product card URL. Swym product objects expose `du` (product
  // URL) and/or a handle; prefer the URL and append the view param.
  function cardUrlFor(product) {
    var url = product.du || product.url || '';
    if (!url) return null;
    // Strip the origin if Swym returned an absolute URL, keep path + query.
    try {
      var u = new URL(url, window.location.origin);
      url = u.pathname + u.search;
    } catch (e) { /* keep as-is */ }
    // Carry the selected variant so price/swatch reflect the wishlisted variant.
    var variant = product.epi || product.variantId;
    var sep = url.indexOf('?') === -1 ? '?' : '&';
    var qs = 'view=wishlist-card' + (variant ? '&variant=' + encodeURIComponent(variant) : '');
    return url + sep + qs;
  }

  function fetchCard(product) {
    var url = cardUrlFor(product);
    if (!url) return Promise.resolve(null);
    return fetch(url)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('[data-wishlist-item]');
      })
      .catch(function () { return null; });
  }

  // All wishlist cards with extracted metadata, used by filter/sort. Order in
  // this array is the original wishlist order ("recently added").
  var cards = [];

  // Pull filter/sort metadata out of a rendered card node.
  function readCardMeta(node, product, index) {
    // Price: prefer the actual selling price. On sale, that's .price-item--sale;
    // otherwise the regular price. Parse the visible currency text.
    var priceText = '';
    var priceEl = node.querySelector('.price__sale .price-item--sale')
      || node.querySelector('.price__regular .price-item--regular')
      || node.querySelector('.price-item--regular, .price-item');
    if (priceEl) priceText = priceEl.textContent || '';
    var price = parseFloat(priceText.replace(/[^0-9.,]/g, '').replace(/,/g, '')) || 0;

    // Filter metadata comes from data-* the wishlist-card view emits, mirroring
    // the collection facets: category, vendor, availability, colors, sizes, function.
    function attrList(name) {
      var raw = node.getAttribute(name) || '';
      return raw ? raw.split('~~').map(function (s) { return s.trim(); }).filter(Boolean) : [];
    }

    var category = (node.getAttribute('data-wl-category') || '').trim();
    var vendor = (node.getAttribute('data-wl-vendor') || '').trim();
    var available = (node.getAttribute('data-wl-available') || '').toLowerCase() === 'true';
    var sizes = attrList('data-wl-sizes');

    // Function is a metaobject metafield; the card already renders its resolved
    // values as a comma-separated list in .card__metafield-text — read it there.
    var functions = [];
    var fnEl = node.querySelector('.card__metafield-text');
    if (fnEl) {
      functions = (fnEl.textContent || '').split(',')
        .map(function (s) { return s.trim(); }).filter(Boolean);
    }

    // Colors: pair each option color name with its swatch background for chips.
    var colorNames = attrList('data-wl-colors');
    var colors = [];
    var swatchMap = {};
    node.querySelectorAll('color-swatch, .color-swatch').forEach(function (sw) {
      var name = (sw.getAttribute('title') || '').trim();
      if (!name || swatchMap[name.toLowerCase()]) return;
      swatchMap[name.toLowerCase()] = true;
      colors.push({ name: name, bg: sw.style.getPropertyValue('--swatch--background') || '' });
    });
    // Include any option colors that didn't render a swatch (fallback to name).
    colorNames.forEach(function (name) {
      if (!swatchMap[name.toLowerCase()]) {
        swatchMap[name.toLowerCase()] = true;
        colors.push({ name: name, bg: name });
      }
    });

    // Title for name sort.
    var titleEl = node.querySelector('.card-information__text, .card-information__title');
    var title = titleEl ? (titleEl.textContent || '').trim() : (node.getAttribute('data-product-handle') || '');

    var addedAt = (product && (product.dt || product.created || product.epoch)) || 0;

    return { node: node, price: price, category: category, vendor: vendor,
             available: available, colors: colors, sizes: sizes, functions: functions,
             title: title, addedAt: addedAt, index: index };
  }

  function render(products) {
    if (!products || !products.length) {
      setStateEmpty();
      return;
    }

    // Fetch all cards in parallel, preserving wishlist order.
    Promise.all(products.map(fetchCard)).then(function (nodes) {
      cards = [];
      nodes.forEach(function (node, i) {
        if (node) {
          var imported = document.importNode(node, true);
          cards.push(readCardMeta(imported, products[i], i));
        }
      });
      if (!cards.length) {
        setStateEmpty();
        return;
      }
      buildFilterUI();
      applyFiltersAndSort();
      setStateGrid();
      show(facetsBar, true);
    });
  }

  /* ---------------- Filter + sort engine (client-side) ---------------- */

  var facetsBar = document.querySelector('[data-wishlist-facets]');
  var countLabels = document.querySelectorAll('[data-wishlist-count-label]');
  var facetsMain = document.querySelector('[data-wishlist-facets-main]');
  // Active filter state.
  var active = { categories: {}, colors: {}, sizes: {}, functions: {}, vendors: {}, inStockOnly: false };
  var sortMode = 'manual';

  function uniqueSorted(values) {
    var seen = {}, out = [];
    values.forEach(function (v) { if (v && !seen[v]) { seen[v] = 1; out.push(v); } });
    return out.sort();
  }

  // A collapsible filter group matching the theme's accordion facet markup.
  function accordionGroup(title, index, innerHtml) {
    return '<details class="mobile-facets__details js-filter mobile-facets__details--accordion" data-index="wishlist-' + index + '">' +
      '<summary class="mobile-facets__summary" role="button"><span><span>' + escapeHtml(title) + '</span>' +
      '<span class="mobile-facets__toggle no-js-hidden">' +
      '<svg aria-hidden="true" focusable="false" class="icon icon-plus" viewBox="0 0 24 24"><path d="M12 6V18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/><path d="M6 12H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>' +
      '<svg aria-hidden="true" focusable="false" class="icon icon-minus" viewBox="0 0 24 24"><path d="M6 12H18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>' +
      '</span></span></summary>' +
      '<div class="mobile-facets__accordion-content">' + innerHtml + '</div></details>';
  }

  // Render a plain checkbox-list filter group (Category, Size, Function, Brand).
  function checkboxGroup(title, key, values, index) {
    if (!values.length) return '';
    var html = '<ul class="facets__presentation--text mobile-facets__list list-unstyled" role="list">';
    values.forEach(function (v, i) {
      var id = 'WishlistFilter-' + key + '-' + i;
      html += '<li class="mobile-facets__item list-menu__item">' +
        '<label for="' + id + '" class="mobile-facets__label" title="' + escapeAttr(v) + '">' +
        '<input class="mobile-facets__checkbox" type="checkbox" data-wfilter="' + key + '" value="' + escapeAttr(v) + '" id="' + id + '">' +
        '<span class="icon"></span>' + escapeHtml(v) + '</label></li>';
    });
    return accordionGroup(title, index, html + '</ul>');
  }

  function buildFilterUI() {
    if (!facetsMain) return;

    // Gather facet values across all cards (mirrors the collection facets:
    // Category, Color, Size, Function, Brand, Availability — no Price).
    var colorMap = {}, categories = [], sizes = [], functions = [], vendors = [];
    cards.forEach(function (c) {
      c.colors.forEach(function (col) {
        if (!colorMap[col.name.toLowerCase()]) colorMap[col.name.toLowerCase()] = col;
      });
      if (c.category) categories.push(c.category);
      if (c.vendor) vendors.push(c.vendor);
      c.sizes.forEach(function (s) { sizes.push(s); });
      c.functions.forEach(function (f) { functions.push(f); });
    });

    var colors = Object.keys(colorMap).map(function (k) { return colorMap[k]; })
      .sort(function (a, b) { return a.name.localeCompare(b.name); });

    var groups = '';
    var gi = 1;

    // Category.
    groups += checkboxGroup('Category', 'category', uniqueSorted(categories), gi++);

    // Color (swatch list, matching .facets__list--swatch markup).
    if (colors.length) {
      var colorHtml = '<ul class="facets__presentation--text mobile-facets__list facets__list--swatch facets__list--swatch-label list-unstyled" role="list">';
      colors.forEach(function (col, i) {
        var id = 'WishlistFilter-color-' + i;
        colorHtml += '<li class="mobile-facets__item list-menu__item">' +
          '<label for="' + id + '" class="mobile-facets__label" title="' + escapeAttr(col.name) + '">' +
          '<input class="mobile-facets__checkbox" type="checkbox" data-wfilter="color" value="' + escapeAttr(col.name) + '" id="' + id + '">' +
          '<span class="color__swatch" style="--swatch--background: ' + escapeAttr(col.bg || col.name) + ';"><span class="color__swatch-dot"></span></span>' +
          '<span class="color__swatch-label">' + escapeHtml(col.name) + '</span></label></li>';
      });
      colorHtml += '</ul>';
      groups += accordionGroup('Color', gi++, colorHtml);
    }

    // Size, Function, Brand.
    groups += checkboxGroup('Size', 'size', uniqueSorted(sizes), gi++);
    groups += checkboxGroup('Function', 'function', uniqueSorted(functions), gi++);
    groups += checkboxGroup('Brand', 'vendor', uniqueSorted(vendors), gi++);

    // Availability.
    var availHtml = '<ul class="facets__presentation--text mobile-facets__list list-unstyled" role="list">' +
      '<li class="mobile-facets__item list-menu__item"><label for="WishlistFilter-instock" class="mobile-facets__label">' +
      '<input class="mobile-facets__checkbox" type="checkbox" data-wfilter="instock" id="WishlistFilter-instock">' +
      '<span class="icon"></span>In stock</label></li></ul>';
    groups += accordionGroup('Availability', gi++, availHtml);

    // Append after the (already-present) sort group.
    facetsMain.insertAdjacentHTML('beforeend', groups);
  }

  function readMulti(key) {
    var set = {};
    facetsMain.querySelectorAll('[data-wfilter="' + key + '"]:checked').forEach(function (i) {
      set[i.value.toLowerCase()] = true;
    });
    return set;
  }

  function readActiveFromUI() {
    active = { categories: {}, colors: {}, sizes: {}, functions: {}, vendors: {}, inStockOnly: false };
    if (!facetsMain) return;
    active.categories = readMulti('category');
    active.colors = readMulti('color');
    active.sizes = readMulti('size');
    active.functions = readMulti('function');
    active.vendors = readMulti('vendor');
    var inStock = facetsMain.querySelector('[data-wfilter="instock"]');
    active.inStockOnly = !!(inStock && inStock.checked);
  }

  // True if the card has at least one value (lowercased) in the active set.
  function matchesAny(activeSet, values) {
    if (!Object.keys(activeSet).length) return true; // no filter on this facet
    return values.some(function (v) { return activeSet[String(v).toLowerCase()]; });
  }

  function cardPasses(c) {
    if (!matchesAny(active.categories, c.category ? [c.category] : [])) return false;
    if (!matchesAny(active.colors, c.colors.map(function (col) { return col.name; }))) return false;
    if (!matchesAny(active.sizes, c.sizes)) return false;
    if (!matchesAny(active.functions, c.functions)) return false;
    if (!matchesAny(active.vendors, c.vendor ? [c.vendor] : [])) return false;
    if (active.inStockOnly && !c.available) return false;
    return true;
  }

  function sortedCards(list) {
    var arr = list.slice();
    switch (sortMode) {
      case 'price-asc': arr.sort(function (a, b) { return a.price - b.price; }); break;
      case 'price-desc': arr.sort(function (a, b) { return b.price - a.price; }); break;
      case 'title-asc': arr.sort(function (a, b) { return a.title.localeCompare(b.title); }); break;
      default: arr.sort(function (a, b) { return a.index - b.index; }); // manual / recently added
    }
    return arr;
  }

  function updateCountLabel(n) {
    // The heading text ("My Wishlist") is static in the markup; only fill the
    // "(n items)" suffix span so the h1 text is never wiped.
    var unit = n === 1 ? 'item' : 'items';
    countLabels.forEach(function (el) {
      var suffix = el.querySelector('[data-count-suffix]');
      if (suffix) {
        suffix.textContent = '(' + n + ' ' + unit + ')';
        suffix.hidden = false;
      }
    });
  }

  // Selected-filters summary in the drawer footer (mirrors the collection's
  // "N filters selected" heading + removable tags).
  var selectedWrap = document.querySelector('[data-wishlist-selected]');
  var selectedHeading = document.querySelector('[data-wishlist-selected-heading]');
  var selectedTags = document.querySelector('[data-wishlist-selected-tags]');

  function renderSelectedFilters() {
    if (!selectedWrap || !facetsMain) return;
    var checked = facetsMain.querySelectorAll('[data-wfilter]:checked');

    if (!checked.length) {
      selectedWrap.classList.add('hidden');
      return;
    }

    // Same wording as the collection drawer ("1 filter selected" / "2 filters selected").
    selectedHeading.textContent =
      checked.length + (checked.length === 1 ? ' filter selected' : ' filters selected');
    var html = '';
    checked.forEach(function (input) {
      var label = input.getAttribute('data-wfilter') === 'instock' ? 'In stock' : input.value;
      // Same markup shape as the collection's tags (span label + icon-close).
      html += '<button type="button" class="mobile-facets__selected-tag" data-tag-for="' + escapeAttr(input.id) + '">' +
        '<span>' + escapeHtml(label) + '</span>' +
        '<svg aria-hidden="true" focusable="false" class="icon icon-close" viewBox="0 0 24 24" fill="none">' +
        '<path d="M6 6L18 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/>' +
        '<path d="M18 6L6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" fill="none"/></svg>' +
        '</button>';
    });
    selectedTags.innerHTML = html;
    selectedWrap.classList.remove('hidden');
  }

  if (selectedTags) {
    selectedTags.addEventListener('click', function (e) {
      var tag = e.target.closest('[data-tag-for]');
      if (!tag) return;
      var input = document.getElementById(tag.getAttribute('data-tag-for'));
      if (input) input.checked = false;
      applyFiltersAndSort();
    });
  }

  function applyFiltersAndSort() {
    readActiveFromUI();
    var visible = cards.filter(cardPasses);
    var ordered = sortedCards(visible);

    grid.innerHTML = '';
    if (!ordered.length) {
      grid.innerHTML = '<li class="wishlist-facets__no-results">No items match these filters.</li>';
    } else {
      ordered.forEach(function (c) { grid.appendChild(c.node); });
    }
    updateCountLabel(ordered.length);
    renderSelectedFilters();
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
  function escapeAttr(s) {
    return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  // Wire controls: filtering + sorting happen live on change (like the
  // collection drawer), all client-side.
  if (facetsMain) {
    facetsMain.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if (t.name === 'wishlist_sort') {
        sortMode = t.value;
        applyFiltersAndSort();
      } else if (t.hasAttribute && t.hasAttribute('data-wfilter')) {
        applyFiltersAndSort();
      }
    });
  }

  var clearBtn = document.querySelector('[data-wishlist-filter-clear]');
  if (clearBtn) clearBtn.addEventListener('click', function () {
    if (facetsMain) {
      facetsMain.querySelectorAll('[data-wfilter]').forEach(function (i) {
        if (i.type === 'checkbox') i.checked = false; else i.value = '';
      });
    }
    applyFiltersAndSort();
  });

  var didFetch = false;
  function fetchWishlist() {
    if (!window._swat || typeof window._swat.fetch !== 'function') return false;
    // Both the swym-loaded event and the poll call this; only run once so the
    // loading spinner isn't re-shown over already-rendered cards.
    if (didFetch) return true;
    didFetch = true;
    setStateLoading();
    window._swat.fetch(function (products) {
      render(products || []);
    }, function () {
      setStateEmpty();
    });
    return true;
  }

  // Removal: every item here is already wishlisted, so our heart button removes
  // it. We call Swym's API directly and drop the card — no reliance on Swym
  // injecting/re-scanning its own button onto these dynamically-fetched cards.
  function dropCardById(productId) {
    if (productId == null) return false;
    var before = cards.length;
    cards = cards.filter(function (c) {
      return c.node.getAttribute('data-product-id') !== String(productId);
    });
    if (cards.length === before) return false;
    if (!cards.length) {
      show(facetsBar, false);
      setStateEmpty();
    } else {
      applyFiltersAndSort();
    }
    return true;
  }

  function removeFromEvent(detail) {
    if (!detail) return;
    // Swym detail shapes vary by SDK version — try the common product-id keys.
    var pid = detail.empi || (detail.product && (detail.product.empi || detail.product.id)) ||
              detail.di || detail.epi;
    dropCardById(pid);
  }

  // Track everything attached to document / timers so the next re-init (for a
  // freshly injected grid) can detach them and not leak or fire on stale DOM.
  var docListeners = [];
  function onDoc(evt, handler) {
    document.addEventListener(evt, handler);
    docListeners.push([evt, handler]);
  }

  // Removal happens through the global heart toggle (wishlist-toggle.js): it
  // calls Swym's API and dispatches swym:removed-from-wishlist; we drop the
  // matching card here. Also covers removals from other surfaces if Swym
  // fires its own events (names vary by SDK).
  ['swym:removed-from-wishlist', 'swym:wishlist-removed', 'swym:remove-from-list',
   'swym:collections-updated'].forEach(function (evt) {
    onDoc(evt, function (e) { removeFromEvent(e && e.detail); });
  });

  // Init: Swym fires `swym-loaded` when its SDK is ready; also poll as a fallback.
  setStateLoading();
  onDoc('swym-loaded', fetchWishlist);

  var tries = 0;
  var poll = setInterval(function () {
    tries += 1;
    if (fetchWishlist()) {
      clearInterval(poll);
    } else if (tries > 30) {
      // Swym never initialised — it sits behind the cookie-consent manager, so
      // a visitor who declined (or is blocking it) gets here. Show the empty
      // state rather than leaving the spinner running forever.
      clearInterval(poll);
      setStateEmpty();
    }
  }, 400);

  // Teardown for this init instance.
  return function () {
    clearInterval(poll);
    docListeners.forEach(function (pair) {
      document.removeEventListener(pair[0], pair[1]);
    });
    docListeners = [];
  };
  } // end init()
})();
